// auditpm-v7
import React, { useEffect, useState } from 'react'
import { oneLine } from '../lib/oneLine'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'
import { FirmHeader } from '../components/PublicComponents'
import { quoteSchemeTitle, quoteShowsWshcSubmission } from '../lib/quoteTitle'
import { emailHtml } from '../lib/emailHtml'
import { isIAQuote, isOTEQuote } from '../lib/service'
import { iaQuoteRows, iaContractYears } from '../lib/iaQuote'
import { oteQuoteRows } from '../lib/oteQuote'

// Each service has its own terms page.
const termsHref = (quote) => (quote && isIAQuote(quote) ? '/terms/internal-audit' : quote && isOTEQuote(quote) ? '/terms/ote' : '/terms')
import { toast, failed } from '../lib/toast'

function fmtMoney(n) {
  return n ? `S$${Number(n).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'
}
function mdLabel(n) {
  const v = parseFloat(n)
  if (!v) return ''
  return `${v} man-day${v === 1 ? '' : 's'}`
}

// ─── Shared quotation body — used for both signing view and signed certificate ─
function QuotationBody({ quote, client }) {
  const r = quote.form_responses || {}
  const companyName    = r.company_name    || client?.name          || quote.prospect_name || ''
  const companyAddress = oneLine(r.company_address || client?.address || '')   // one line (older ones had breaks)
  const picName        = r.pic_name        || client?.contact_name  || ''
  const picPhone       = r.pic_phone       || client?.contact_phone || ''
  const picEmail       = r.pic_email       || client?.contact_email || ''
  const businessNature = r.business_nature || client?.notes         || ''
  const numEmp         = r.num_employees   || '—'
  const md             = mdLabel(quote.mandays)
  // Expiry date covers both renewal and progression cases
  const certExpiry = r.expiry_date || r.expiry_date_progression || null
  const auditScope     = r.audit_scope || quote.audit_scope || ''

  const rowStyle = {
    display: 'grid', gridTemplateColumns: '160px 1fr',
    gap: '8px 16px', fontSize: 13,
    padding: '10px 0', borderBottom: '0.5px solid #f0eff0',
    alignItems: 'start',
  }
  const labelStyle = { color: '#888', fontWeight: 500, paddingTop: 1 }
  const valueStyle = { color: '#222', whiteSpace: 'pre-line', lineHeight: 1.6 }

  return (
    <div>
      {/* ── Header ── (client internal audit is not an accredited service: no SAC line) */}
      <FirmHeader accredited={!isIAQuote(quote)} />

      {/* ── To / Date block ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>To</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111' }}>{companyName}</div>
          {companyAddress && <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>{companyAddress}</div>}
          {picName        && <div style={{ fontSize: 13, color: '#555', marginTop: 6 }}>{picName}</div>}
          {picPhone       && <div style={{ fontSize: 13, color: '#555' }}>T: {picPhone}</div>}
          {picEmail       && <div style={{ fontSize: 13, color: '#555' }}>E: {picEmail}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 20 }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Quotation</div>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#006363' }}>{quote.quote_ref}</div>
          {quote.sent_date && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              {format(parseISO(quote.sent_date), 'd MMM yyyy')}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
            Valid {quote.validity_days || 30} days
          </div>
        </div>
      </div>

      {/* ── Subject line ── */}
      <div style={{ padding: '10px 14px', background: '#006363', borderRadius: 8, marginBottom: 20 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
          {quoteSchemeTitle(quote)}
        </div>
      </div>

      {/* ── Quotation details ── */}
      <div style={{ background: '#fff', borderRadius: 10, border: '0.5px solid #e8e8e8', overflow: 'hidden', marginBottom: 16 }}>
        {/* Fee — highlighted */}
        <div style={{ background: '#f7fbf7', borderBottom: '0.5px solid #e8e8e8', padding: '14px 20px' }}>
          <div style={{ ...rowStyle, border: 'none', padding: 0 }}>
            <div style={labelStyle}>Quoted fee</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#006363' }}>{fmtMoney(quote.fee_ex_gst)} <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>excl. GST</span></div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{fmtMoney(quote.fee_inc_gst)} incl. 9% GST · per audit</div>
              {isIAQuote(quote) && iaContractYears(quote) > 1 && (
                <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
                  {iaContractYears(quote)} audits over {iaContractYears(quote)} years — {fmtMoney((parseFloat(quote.fee_ex_gst) || 0) * iaContractYears(quote))} excl. GST in total (invoiced yearly)
                </div>)}
            </div>
          </div>
        </div>

        <div style={{ padding: '0 20px' }}>
          {(isIAQuote(quote) ? iaQuoteRows(quote, client) : isOTEQuote(quote) ? oteQuoteRows(quote, client) : [
            ['Scope of service', `WSH Risk Management Audit (${md})\n• Conduct audit by SAC Accredited Auditing Organisation\n• Issuance of WSH RM Audit report${quoteShowsWshcSubmission(quote) ? '\n• Submission of report to WSHC' : ''}`],
            auditScope ? ['Audit scope', auditScope] : null,
            businessNature ? ['Business nature', businessNature] : null,
            ['No. of employees', numEmp],
            certExpiry ? ['Current cert expiry', format(parseISO(certExpiry), 'd MMMM yyyy')] : null,
            ['Payment terms', '100% upfront payment\nPayNow / GIRO preferred — UEN 199205611D'],
            ['Timeline', quote.timeline || 'Approximately 2 months from confirmation'],
            ['Remarks', `• Ensure your company is NOT SgMA certified by SCAL\n• Please confirm your preferred audit date at least 1 month in advance\n• For Level 3 or 4 — Level 1 and 2 certificate holders must be present on audit day${quote.remarks ? '\n• ' + quote.remarks : ''}`],
          ]).filter(Boolean).map(([label, value]) => (
            <div key={label} style={rowStyle}>
              <div style={labelStyle}>{label}</div>
              <div style={valueStyle}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Terms link ── */}
      <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>
        This quotation is subject to our{' '}
        <a href={termsHref(quote)} target="_blank" rel="noreferrer" style={{ color: '#006363', fontWeight: 700 }}>
          {isIAQuote(quote) || isOTEQuote(quote) ? 'Terms & Conditions' : <>Roles, Conditions &amp; Limitations</>}
        </a>.
      </div>
    </div>
  )
}

// ─── Signed certificate ───────────────────────────────────────────────────────
function SignedCertificate({ quote, client, auditTrail }) {
  const trail = auditTrail || quote.audit_trail || {}
  const r = quote.form_responses || {}
  const companyName = r.company_name || client?.name || quote.prospect_name || ''

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <QuotationBody quote={quote} client={client} />

      <div style={{ marginTop: 24, padding: '20px 24px', background: '#EAF3DE', borderRadius: 10, border: '0.5px solid #639922' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#27500a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>✓</span>
          </div>
          <div style={{ fontWeight: 600, color: '#27500a', fontSize: 15 }}>Acceptance Certificate</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '6px 16px', fontSize: 13 }}>
          {[
            ['Quotation ref',   quote.quote_ref],
            ['Organisation',    companyName],
            ['Accepted by',     trail.signatory_name || quote.signatory_name || '—'],
            ['Designation',     trail.signatory_designation || quote.signatory_designation || '—'],
            ['Date & time',     trail.signed_at ? new Date(trail.signed_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' }) : quote.signed_date || '—'],
            ['IP address',      trail.ip_address || '—'],
          ].map(([l, v]) => (
            <React.Fragment key={l}>
              <span style={{ color: '#3b6d11', fontWeight: 500 }}>{l}</span>
              <span style={{ color: '#27500a', fontWeight: l === 'Accepted by' || l === 'Organisation' ? 600 : 400, wordBreak: 'break-word' }}>{v}</span>
            </React.Fragment>
          ))}
          <span style={{ color: '#3b6d11', fontWeight: 500 }}>Browser</span>
          <span style={{ color: '#27500a', fontSize: 11, wordBreak: 'break-all' }}>{trail.user_agent ? trail.user_agent.slice(0, 120) : '—'}</span>
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: '#3b6d11', borderTop: '0.5px solid #97c459', paddingTop: 10, lineHeight: 1.6 }}>
          This document confirms digital acceptance of the above quotation. Recorded by CCIS Singapore Pte Ltd's secure system with full audit trail — IP address, timestamp and device information.
        </div>
      </div>

      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <button onClick={() => window.print()} style={{ padding: '8px 20px', borderRadius: 8, border: '0.5px solid rgba(0,0,0,0.2)', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
          🖨️ Print / Save as PDF
        </button>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function QuotationSign() {
  const { token } = useParams()
  const [quote, setQuote]       = useState(null)
  const [client, setClient]     = useState(null)
  const [invalid, setInvalid]   = useState(false)
  const [loading, setLoading]   = useState(true)
  const [signed, setSigned]     = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm]         = useState({ name: '', designation: '', agree: false })
  const [auditTrail, setAuditTrail] = useState(null)
  const [blockedMsg, setBlockedMsg] = useState(null)

  useEffect(() => { load() }, [token])

  async function load() {
    // Token-scoped read via RPC (no broad anon table access)
    const { data: q, error } = await supabase.rpc('get_quote_by_token', { p_token: token })
    if (error || !q || !q.id) { setInvalid(true); setLoading(false); return }
    if (q.status === 'Signed') {
      setSigned(true); setAuditTrail(q.audit_trail)
    } else if (q.status === 'Superseded') {
      let latest = null
      try { const { data } = await supabase.rpc('get_latest_quote_ref', { p_token: token }); latest = data } catch (e) {}
      let msg = 'This quotation version has been superseded and is no longer valid.'
      if (latest && latest !== q.quote_ref) msg += ` The current version is ${latest}.`
      msg += ' Please contact us if you require the latest version.'
      setBlockedMsg(msg)
    } else if (q.status === 'Declined') {
      setBlockedMsg('This quotation is no longer active. Please contact us if you wish to proceed.')
    } else if (q.status === 'Cancelled') {
      // Signed, then cancelled: the signed agreement stays viewable as a record (never signable again).
      setSigned(true); setAuditTrail(q.audit_trail)
    } else if (q.status === 'Withdrawn') {
      setBlockedMsg('This quotation has been withdrawn. Please contact us for an updated quotation.')
    } else if (q.status === 'Sent' && q.sent_date && q.validity_days
      && (Date.now() - new Date(q.sent_date).getTime()) / 86400000 > q.validity_days) {
      setBlockedMsg('This quotation has expired and can no longer be signed. Please contact us for an updated quotation.')
    }
    setQuote(q); setClient(q.clients); setLoading(false)
  }

  async function sign() {
    if (!form.name || !form.designation || !form.agree) {
      toast('Please complete your name and designation', 'warning', { detail: 'And tick the agreement' })
      return
    }
    setSubmitting(true)

    let ipAddress = 'unknown'
    try { const d = await (await fetch('https://api.ipify.org?format=json')).json(); ipAddress = d.ip } catch(e) {}

    // Client upsert + quote update + single job creation happen server-side
    const { data, error } = await supabase.rpc('sign_quotation', {
      p_token: token,
      p_name: form.name,
      p_designation: form.designation,
      p_ip: ipAddress,
      p_ua: navigator.userAgent,
    })
    if (error) {
      failed("We couldn't record your acceptance", error.message, 'Please try again')
      setSubmitting(false)
      return
    }

    setAuditTrail(data?.trail || null); setSigned(true); setSubmitting(false)
    // Best-effort internal notification to Accounts (invoicing). NEVER blocks signing.
    try {
      await supabase.functions.invoke('send-internal-email', {
        body: {
          to: 'accounts@ccis.com.sg',           // signed-quote notice goes straight to Accounts
          subject: `Quotation Signed – ${data?.quote_ref || ''} – ${data?.company_name || ''}`,
          html: emailHtml({
            title: 'Quotation signed',
            greeting: 'Dear Accounts,',
            lines: ['The following quotation has been signed by the client online. Please arrange invoicing and update the CRM records accordingly.'],
            details: [['Quotation reference', data?.quote_ref || '—'], ['Company', data?.company_name || '—'],
              ['Audit type', data?.audit_type || '—'], ['Signed date', data?.signed_date || new Date().toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Singapore' })]],
            button: { label: 'Open job →', url: `${window.location.origin}${data?.job_id ? `/jobs/${data.job_id}` : '/jobs'}` },
          }),
        },
      })
    } catch (e) { console.error('sign notification failed (non-blocking):', e) }
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3' }}>
      <div style={{ color: '#999' }}>Loading…</div>
    </div>
  )

  if (invalid) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 420, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Invalid link</h2>
        <p style={{ color: '#666', fontSize: 14 }}>This link is not valid or has expired. Please contact us.</p>
      </div>
    </div>
  )

  if (blockedMsg) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 460, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Quotation no longer valid</h2>
        <p style={{ color: '#666', fontSize: 14, lineHeight: 1.7 }}>{blockedMsg}</p>
        <p style={{ color: '#888', fontSize: 13, marginTop: 12 }}>CCIS Singapore Pte Ltd · query@ccis.com.sg</p>
      </div>
    </div>
  )

  if (signed && quote) return (
    <div style={{ minHeight: '100vh', background: '#f7f6f3', padding: '40px 20px 60px' }}>
      {quote.status === 'Cancelled' && (
        <div style={{ maxWidth: 720, margin: '0 auto 14px', background: '#fdecec', border: '0.5px solid #e24b4a', borderRadius: 12, padding: '14px 20px', color: '#791f1f', fontSize: 14 }}>
          <strong>This contract has been cancelled.</strong> The signed agreement below is kept for your records. Please contact us if you wish to proceed.
        </div>)}
      <div style={{ maxWidth: 720, margin: '0 auto', background: '#fff', borderRadius: 12, padding: '36px 40px', border: '0.5px solid rgba(0,0,0,0.1)' }}>
        <SignedCertificate quote={quote} client={client} auditTrail={auditTrail} />
      </div>
      <p style={{ textAlign: 'center', color: '#aaa', fontSize: 12, marginTop: 16 }}>
        Bookmark this page or print it for your records.
      </p>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#f7f6f3', padding: '40px 20px 60px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* Quotation document */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '36px 40px', border: '0.5px solid rgba(0,0,0,0.1)', marginBottom: 16 }}>
          <QuotationBody quote={quote} client={client} />
        </div>

        {/* Acceptance block */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '28px 40px', border: '0.5px solid rgba(0,0,0,0.1)' }}>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Agreed, Confirmed &amp; Accepted By</div>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
            By signing below you confirm you are authorised to accept this quotation on behalf of your organisation.
          </p>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>Full name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              style={{ width: '100%', padding: '9px 12px', border: '0.5px solid #ccc', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>Designation *</label>
            <input value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))}
              style={{ width: '100%', padding: '9px 12px', border: '0.5px solid #ccc', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 24, padding: '12px 14px', background: '#f7f6f3', borderRadius: 8 }}>
            <input type="checkbox" checked={form.agree} onChange={e => setForm(f => ({ ...f, agree: e.target.checked }))} style={{ marginTop: 2, flexShrink: 0, width: 'auto', accentColor: '#006363' }} />
            <span style={{ fontSize: 13, color: '#444', lineHeight: 1.6 }}>
              I have read and agree to the quotation above and CCIS Singapore's{' '}
              <a href={termsHref(quote)} target="_blank" rel="noreferrer" style={{ color: '#006363', fontWeight: 700 }}>{quote && (isIAQuote(quote) || isOTEQuote(quote)) ? 'Terms & Conditions' : <>Roles, Conditions &amp; Limitations</>}</a>.
              By submitting, I confirm this is a binding acceptance.
            </span>
          </label>

          <button
            onClick={sign}
            disabled={!form.name || !form.designation || !form.agree || submitting}
            style={{
              width: '100%', padding: '13px 20px', borderRadius: 10, border: 'none',
              background: form.name && form.designation && form.agree ? '#006363' : '#ccc',
              color: '#fff', fontWeight: 600, fontSize: 15, cursor: form.name && form.designation && form.agree ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit', letterSpacing: 0.2,
            }}>
            {submitting ? 'Processing…' : 'Accept & sign quotation →'}
          </button>
        </div>

      </div>
    </div>
  )
}
